<script context="module" lang="ts">
	// your script goes here
	export type SaveDetail = {
		question: string;
		answer: string;
		documentState: string;
		origin: FAQ;
	};
</script>

<script lang="ts">
	//UI
	import { Card, SlotSelect, DefaultButton } from "@hitit/new_ui";
	import ComponentLoading from "@hitit/hub/src/v7/components/common/ComponentLoading.svelte";
	import PlainTextEditor from "@hitit/hub/src/components/place/PlainTextEditor.svelte";
	import Wiswig from "@hitit/wiswig/src/MCE/TinyMCE.svelte";
	//Utils
	import { style_formats, default_conf } from "@hitit/wiswig/src/MCE/constants";
	import { createEventDispatcher } from "svelte";
	import {
		imageSetup,
		linkSetup,
		browserSizeMatch,
		forecolorSetup,
		completedUploadCallback,
	} from "@hitit/wiswig/src/MCE/utils";

	//Types
	import type { ActiveEditor } from "@hitit/wiswig/src/MCE/types";
	import type { FAQ } from "../../class/FAQ/FAQ_Manager";

	//Props

	export let item: FAQ;
	//Constants
	const dispatch = createEventDispatcher();
	const apiKey = "nqo1wyruv063uttoar1ywjr2hkey7nuca5ho7e4zne55govv";
	const selectOptions = ["OBSOLETE", "PUBLISHED", "DRAFT"].map((v) => ({
		label: v,
		value: v,
		display: v,
	}));
	const keyboardOption = {
		bindings: {
			shift_enter: {
				key: 13,
				shiftKey: true,
				handler: () => {},
			},
			enter: {
				key: 13,
				handler: () => {},
			},
		},
	};
	const conf = Object.assign({}, default_conf, {
		style_formats,
		setup,
		toolbar_mode: "wrap",
		content_css: "/wiswig.css",
	});

	//Variables
	let initialQuestion = "";
	let initialAnswer = "";
	let initialDocumentState = "";
	let question = "";
	let answer = "";
	let documentState = "PUBLISHED";
	let wiswigReady = false;
	let id = "";
	let displaySort = 0;

	//Functions
	function init(item: FAQ) {
		if (item.id === id) {
			displaySort = item.displaySort;
		} else {
			id = item.id as string;
			displaySort = item.displaySort;
			initialQuestion = item.question;
			initialAnswer = item.answer || "";
			initialDocumentState = item.documentState;
			question = item.question;
			answer = item.answer || "";
			documentState = item.documentState;
		}
	}

	function questionHandler(e: { detail: { text: string } }) {
		question = e.detail.text;
	}

	function setup(editor: ActiveEditor) {
		linkSetup(editor);
		browserSizeMatch(editor);
		forecolorSetup(editor);
		imageSetup(editor, completedUploadCallback);
		editor.on("init", () => {
			wiswigReady = true;
		});
	}

	function saveHandler() {
		dispatch("SAVE", {
			question,
			answer,
			documentState,
			origin: item,
		});
	}

	//Do
	$: init(item);

	$: disabled =
		(initialQuestion.trim() === question.trim() &&
			initialAnswer === answer &&
			initialDocumentState === documentState) ||
		question.trim() === "" ||
		answer === "" ||
		answer === "<p></p>";
</script>

<Card noPadding -faqeditor-card>
	<div class="container" id="single-editor">
		<div class="header">
			<span class="displaysort"> {displaySort} </span>
			<div class="status">
				<SlotSelect
					bind:value={documentState}
					-select
					svg
					backgroundColor="#f0f0f4"
					items={selectOptions}
					let:displayValue
				>
					<div class="select-inner">
						<span
							class="icon"
							class:publish={displayValue === "PUBLISHED"}
							class:obsolate={displayValue === "OBSOLETE"}
						/>

						<span class="value"> {displayValue} </span>
					</div>
				</SlotSelect>
			</div>
		</div>
		<div class="title-values">
			<div class="title-value question">
				<span class="title"> 질문 </span>
				<div class="value question-editor">
					<PlainTextEditor
						on:UPDATE_TEXT={questionHandler}
						lengthLimit={Infinity}
						lineLimit={Infinity}
						keyboard={keyboardOption}
						value={initialQuestion}
						reactiveId={item.id}
						reactiveValue
					/>
				</div>
			</div>
			<div class="title-value answer">
				<span class="title"> 답변 </span>
				<div class="value answer-editor">
					{#if wiswigReady === false}
						<ComponentLoading coverMode />
					{/if}
					<Wiswig apiKey={apiKey} conf={conf} bind:value={answer} />
				</div>
			</div>
		</div>
		<DefaultButton -faq-save={true} on:click={saveHandler} disabled={disabled}>
			<span class="save"> 임시 저장 </span>
		</DefaultButton>
	</div>
</Card>

<style lang="scss">
	@import "../../../variables.scss";
	:global(.-faqeditor-card) {
		overflow-y: auto;
	}
	:global(.-faq-save) {
			display: flex;
			justify-content: center;
			align-items: center;
		}
	.container {
		display: flex;
		flex-direction: column;
		width: 100%;
		padding: 20px 10px;
		height: max-content;
		gap: 20px;
		box-sizing: border-box;
		.save {
			font-weight: 500;
			font-size: 1rem;
			color: white;
			margin-top: -2px;
		}
		.header {
			display: flex;
			justify-content: space-between;
			.displaysort {
				color: #46474b;
				font-weight: 500;
				font-size: 1.25rem;
			}
			.status {
				display: flex;
				gap: 10px;
				color: #46474b;
				font-weight: 200;
				width: 153px;
				height: 32px;
				:global(.-select) {
					padding: 10px;
				}
				.select-inner {
					display: flex;
					gap: 10px;
					align-items: center;
					flex-grow: 1;

					.icon {
						width: 15px;
						height: 15px;
						border-radius: 15px;
						background-color: #bcbfc3;

						&.publish {
							background-color: #44e4ae;
						}

						&.obsolate {
							background-color: #ffce00;
						}
					}
				}
			}
		}
		.title-values {
			display: flex;
			flex-direction: column;
			gap: 20px;

			.title-value {
				display: flex;
				flex-direction: column;
				gap: 10px;

				.title {
					color: #6d6f76;
					font-size: 0.875rem;
					font-weight: 200;
				}
				.question-editor {
					height: 100px;

					:global(.ql-container) {
						height: 100%;
					}
					:global(.ql-editor) {
						height: 100%;
						background-color: #f0f0f4;
						box-sizing: border-box;
						overflow: auto;
						color: #46474b;
						font-weight: 200;
						padding: 10px;
						border-radius: 5px;
						&:focus {
							outline: 0px solid transparent;
						}

						&::-webkit-scrollbar {
							width: 2px;
						}
						&::-webkit-scrollbar-track {
							background-color: transparent;
						}

						&::-webkit-scrollbar-thumb {
							background-color: #bcbfc3;
						}
					}
				}
				.answer-editor {
					height: 400px;
				}

				.value {
					width: 100%;
				}
			}
		}
	}

	@include DesktopSize {
		.container {
			padding: 30px;
			height: 100%;
			.save {
				font-size: 1.25rem;
			}
			.header {
				.displaysort {
					font-size: 1.5rem;
				}
				.status {
					width: 183px;
					height: 40px;
					.select-inner {
						font-size: 1.25rem;
						.icon {
							width: 20px;
							height: 20px;
						}
					}
				}
			}
			.title-values {
				flex-grow: 1;
				.title-value {
					.title {
						font-size: 1rem;
					}

					&.answer {
						flex-grow: 1;
					}

					.question-editor {
						height: 150px;
						:global(.ql-editor) {
							font-size: 1.25rem;
						}
					}
					.answer-editor {
						height: auto;
						flex-grow: 1;
					}
				}
			}
		}
	}
</style>
